import React from "react";
import { Route, createBrowserRouter, createRoutesFromElements, Navigate } from "react-router-dom";

import Login from "../component/auth/Login";
import Set2FA from "../component/Set2FA";
import Dashboard from "../component/Dashboard";
import FeatureRequest from "../component/Feedback";

import PrivateRoute from "./privateRoute";
import Landing from "../component/Landing";
import DataCatalog from "../component/DataCatalog";

import AuthProviderRoutes from "./authProviderRoute";
import MyWorkspaceRoutes from "./myWorkspaceRoute";
import ReportsRoutes from "./reportsRoute";

import PageSuspense from "../component/Common/PageSuspense";
import ErrorBoundry from "../component/Common/ErrorBoundry";
import Wrapper from "./Wrapper";

// Lazily imported modules as they are not required to have in main build

const ForgotPassword = React.lazy(() => import("../component/ForgotPassword"));
const ResetPassword = React.lazy(() => import("../component/ForgotPassword/ResetPassword"));

const ManageWorkspaces = React.lazy(() => import("../component/ManageWorkspaces"));
const MyProfile = React.lazy(() => import("../component/MyProfile"));
const SystemInfo = React.lazy(() => import("../component/SystemInfo/index"));
const UserConnections = React.lazy(() => import("../component/UserConnections"));

const General = React.lazy(() => import("../component/GeneralSettings"));
const CasesRoutes = React.lazy(() => import("./casesRoutes"));
const UserManagementRoutes = React.lazy(() => import("./userManagementRoute"));
const ConnectionRoutes = React.lazy(() => import("./connectionRoute"));

const Sandbox = React.lazy(() => import("../component/Sandbox/index"));

/*
----------------NOTE------------------
Only use 'Workspace_id' route param only for Workspace related route,
outside of Workspace moudle route, if routes need Workspace_id as an param, please use any other name for that param.
For example see `casesRoutes.js`
*/

const AppRoute = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<ErrorBoundry />} element={<Wrapper />}>
      <Route path="/" exact element={<Landing />} />

      <Route
        path="/control-panel"
        exact
        element={
          <PrivateRoute moduleName="OPEN">
            <Dashboard />
          </PrivateRoute>
        }
      />

      <Route
        path="/feedback-suggestions"
        exact
        element={
          <PrivateRoute moduleName="OPEN">
            <FeatureRequest />
          </PrivateRoute>
        }
      />

      <Route path="/signin" element={<Login />} />

      <Route path="/set2FA" element={<Set2FA />} />

      <Route
        path="/forgot-password"
        element={
          <PageSuspense>
            <ForgotPassword />
          </PageSuspense>
        }
      />

      <Route
        path="/reset-password"
        element={
          <PageSuspense>
            <ResetPassword />
          </PageSuspense>
        }
      />

      <Route path="/*" element={<Navigate to="/" />} />

      <Route
        path="/connections/*"
        element={
          <PrivateRoute moduleName="SETTINGS" lazyLoad>
            <ConnectionRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/general"
        element={
          <PrivateRoute moduleName="SETTINGS" lazyLoad>
            <General />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/auth-providers/*"
        element={
          <PrivateRoute moduleName="SETTINGS">
            <AuthProviderRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/user-management/*"
        element={
          <PrivateRoute moduleName="USER_MANAGEMENT" lazyLoad>
            <UserManagementRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/my-workspaces/*"
        element={
          <PrivateRoute moduleName="MY_WORKSPACES">
            <MyWorkspaceRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/manage-workspaces"
        element={
          <PrivateRoute moduleName="MANAGE_WORKSPACES" lazyLoad>
            <ManageWorkspaces />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/my-access-credentials"
        element={
          <PrivateRoute moduleName="ACCESS_CREDENTIALS" lazyLoad>
            <UserConnections />
          </PrivateRoute>
        }
      ></Route>
      <Route
        path="/system-info"
        element={
          <PrivateRoute moduleName="OPEN" lazyLoad>
            <SystemInfo />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/my-profile"
        element={
          <PrivateRoute moduleName="OPEN" lazyLoad>
            <MyProfile />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/reports/*"
        element={
          <PrivateRoute moduleName="REPORTS">
            <ReportsRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/cases/*"
        element={
          <PrivateRoute moduleName="DATA_STEWARDSHIP" lazyLoad>
            <CasesRoutes />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/sandbox/*"
        element={
          <PrivateRoute moduleName="SANDBOX" lazyLoad>
            <Sandbox />
          </PrivateRoute>
        }
      ></Route>

      <Route
        path="/data-catalog"
        element={
          <PrivateRoute moduleName="DATA_CATALOG">
            <DataCatalog />
          </PrivateRoute>
        }
      ></Route>
    </Route>
  )
);

export default AppRoute;
